import React from 'react';
import classNames from 'classnames';
import Spinner from '../../common/Spinner/Spinner';
import { EnvagoAria } from '../../../aria.types';
import './Button.less';

export interface RoundIconButtonProps {
    icon: React.ReactNode; // required icon node
    primary?: boolean;
    secondary?: boolean;
    accent?: boolean;
    ghost?: boolean;
    onClick?: () => void;
    className?: string;
    disabled?: boolean;

    isLoading?: boolean;
    aria?: EnvagoAria; // make sure to pass aria-label for icon-only buttons
    size?: number;
}

const RoundIconButton = ({
    icon,
    primary,
    secondary,
    accent,
    ghost,
    onClick,
    className,
    disabled = false,
    isLoading = false,
    aria,
    size = 40,
}: RoundIconButtonProps) => {
    const spinnerSize = Math.max(14, Math.floor(size));

    const style = {
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
    };

    return (
        <button
            {...aria}
            onClick={() => {
                if (isLoading) return;
                onClick && onClick();
            }}
            onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                if (disabled) return;
                onClick && onClick();
            }}
            disabled={disabled}
            className={classNames('button', 'button--round', className, {
                'button--primary': !disabled && primary,
                'button--secondary': !disabled && secondary,
                'button--accent': !disabled && accent,
                'button--ghost': !disabled && ghost,
                'button--disabled': disabled,
            })}
            style={style}
        >
            <div className="icon-button button-text-wrapper relative">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Spinner size={spinnerSize} variant="oval" />
                    </div>
                )}

                <div
                    className={'relative z-0'}
                    style={{
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    {icon}
                </div>
            </div>
        </button>
    );
};

export default RoundIconButton;
